import { api } from "./api";
import { executarRequisicaoAutenticada } from "./authenticatedRequest";

export interface DisponibilidadeInformeImpostoRenda {
  anoCalendario: number;
  disponivel: boolean;
  comunicacaoClienteId?: number;
  titulo?: string;
  mensagem?: string;
  atualizadoEm?: string;
}

export interface InformeImpostoRendaDownload {
  blob: Blob;
  fileName: string;
}

const parseResponse = (data: any) => {
  if (typeof data !== "string") {
    return data;
  }

  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

const getResultado = <T,>(response: any): T => {
  const data = parseResponse(response?.data);
  return data?.resultado ?? data?.camposAdicionais ?? data;
};

const withAuthorization = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` },
});

const normalizeAvailability = (data: any): DisponibilidadeInformeImpostoRenda => ({
  anoCalendario: data?.anoCalendario ?? data?.AnoCalendario,
  disponivel: Boolean(data?.disponivel ?? data?.Disponivel),
  comunicacaoClienteId: data?.comunicacaoClienteId ?? data?.ComunicacaoClienteId,
  titulo: data?.titulo ?? data?.Titulo,
  mensagem: data?.mensagem ?? data?.Mensagem,
  atualizadoEm: data?.atualizadoEm ?? data?.AtualizadoEm,
});

const parseFileName = (contentDisposition?: string) => {
  if (!contentDisposition) return undefined;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
  }

  const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return fileNameMatch?.[1];
};

export const obterDisponibilidadeInformeImpostoRenda = async (
  anoCalendario?: number
) => {
  const response = await executarRequisicaoAutenticada((token) =>
    api.get("/TaxReport/Disponibilidade", {
      ...withAuthorization(token),
      params: { anoCalendario },
    })
  );

  return normalizeAvailability(getResultado<DisponibilidadeInformeImpostoRenda>(response));
};

export const baixarInformeImpostoRenda = async (
  anoCalendario?: number
): Promise<InformeImpostoRendaDownload> => {
  const response = await executarRequisicaoAutenticada((token) =>
    api.get("/TaxReport/Meu", {
      ...withAuthorization(token),
      params: { anoCalendario },
      responseType: "blob",
    })
  );
  const fileName =
    parseFileName(response.headers?.["content-disposition"]) ||
    `informe-imposto-renda-${anoCalendario || new Date().getFullYear() - 1}.pdf`;

  return {
    blob: response.data,
    fileName,
  };
};

export const baixarInformeImpostoRendaGestor = async ({
  idCredenciaisCliente,
  anoCalendario,
}: {
  idCredenciaisCliente: string;
  anoCalendario?: number;
}): Promise<InformeImpostoRendaDownload> => {
  const response = await executarRequisicaoAutenticada((token) =>
    api.get("/TaxReport", {
      ...withAuthorization(token),
      params: {
        IdCredenciaisCliente: idCredenciaisCliente,
        AnoCalendario: anoCalendario,
      },
      responseType: "blob",
    })
  );
  const fileName =
    parseFileName(response.headers?.["content-disposition"]) ||
    `informe-imposto-renda-${anoCalendario || new Date().getFullYear() - 1}.pdf`;

  return {
    blob: response.data,
    fileName,
  };
};
